import {
  Component,
  effect,
  HostBinding,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ApiErrorService, ToastService } from '../../shared/services';
import { AuthService } from '../../services/auth';
import { AuthenticatedUser, LoginRequest } from '../../shared/models/auth';
import { GoogleAuthService } from '../../services/google-auth.service';
import { MembershipService } from '../../services/membership/membership.service';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.html',
  styleUrls: ['../auth-form.css', './login.css'],
})
export class Login {
  //#region Injections
  authService = inject(AuthService);
  googleAuthService = inject(GoogleAuthService);
  router = inject(Router);
  http = inject(HttpClient);
  toastService = inject(ToastService);
  membershipService = inject(MembershipService);
  apiErrorService = inject(ApiErrorService);
  //#endregion
  loginForm: FormGroup;
  isLogin: boolean = false;
  showPassword = false;
  checkboxToggle = false;

  constructor(private fb: FormBuilder) {
    this.loginForm = this.fb.group({
      // Prefilled when arriving from an accepted team invitation
      email: [inject(ActivatedRoute).snapshot.queryParamMap.get('email') ?? '', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }
  get emailControl(): FormControl {
    return this.loginForm.get('email') as FormControl;
  }

  get passwordControl(): FormControl {
    return this.loginForm.get('password') as FormControl;
  }

  /** A field shows its error once the user has left it or tried to submit */
  showError(control: FormControl): boolean {
    return control.invalid && control.touched;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleCheckbox() {
    this.checkboxToggle = !this.checkboxToggle;
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.isLogin = true;
      const loginPayload = new LoginRequest(
        this.loginForm.value.email,
        this.loginForm.value.password
      );

      this.authService
        .postGeneric<LoginRequest, AuthenticatedUser>(loginPayload)
        .pipe(
          switchMap((res) => {
            if (res && res.token && res.expiresAt) {
              this.authService.login(res);

              // Skip membership check for system administrators
              if (this.authService.isSystemAdmin()) {
                this.router.navigate(['/system-admin']);
                return [];
              }

                  // Verify membership status before navigating
              return this.membershipService.getMembershipStatus().pipe(
                switchMap((status) => {
                  console.log(status);
                  if (status.isExpired) {
                    // Redirect to billing if membership is expired
                    this.router.navigate(['/settings/billing']);
                    return [];
                  }
                  // Navigate to dashboard if membership is valid
                  this.router.navigate(['/dashboard']);
                  return [];
                })
              );
            } else {
              this.toastService.show(
                'Respuesta inválida del servidor.',
                'warning'
              );
              return [];
            }
          })
        )
        .subscribe({
          next: () => {
            this.isLogin = false;
          },
          error: (err: any) => {
            // Only redirect to billing if it's a 402 (Payment Required) error
            // For authentication errors (401, 403), show error message
            // For other errors, navigate normally and let the guard handle it
            if (err?.status === 402) {
              this.router.navigate(['/settings/billing']);
            } else if (err?.status === 401 || err?.status === 403) {
              const apiErrors = err?.error?.errors;
              const message =
                (Array.isArray(apiErrors) && apiErrors.length
                  ? apiErrors.join('<br>')
                  : err?.error?.message) ||
                'Ocurrió un error inesperado. Intenta de nuevo.';
              console.error('Error en login:', err);
              this.toastService.show(message, 'warning');
            } else {
              // For other errors (network, etc.), navigate normally
              console.warn(
                'Error checking membership status, navigating normally:',
                err
              );
              this.router.navigate(['/dashboard']);
            }
            this.isLogin = false;
          },
        });
    } else {
      // Each field explains what is missing, so take the user to the first one
      this.loginForm.markAllAsTouched();
      const firstInvalid = this.emailControl.invalid ? 'email' : 'password';
      document.getElementById(firstInvalid)?.focus();
    }
  }

  async signInWithGoogle() {
    try {
      // Check if Google SDK is loaded
      if (!this.googleAuthService.isGoogleSdkLoaded()) {
        this.toastService.show(
          'Google SDK no está cargado. Intenta recargar la página.',
          'warning'
        );
        return;
      }

      console.log('Attempting Google Sign-In...');

      // Get the token from Google using OAuth2 popup
      const token = await this.googleAuthService.signInWithGoogle();

      console.log('Google token received:', token ? 'Yes' : 'No');

      // Send the token to our backend
      this.authService.authenticateWithGoogle(token).subscribe({
        next: (response: AuthenticatedUser) => {
          console.log('Backend response received:', response);

          // Check if user needs to complete signup
          if (response.requiresSignup && response.googleUserInfo) {
            console.log('✅ User needs to complete signup');
            console.log(
              '📤 Storing Google user info:',
              response.googleUserInfo
            );

            // Store Google data temporarily and redirect to signup form
            sessionStorage.setItem(
              'googleUserInfo',
              JSON.stringify(response.googleUserInfo)
            );

            // Verify it was stored
            const stored = sessionStorage.getItem('googleUserInfo');
            console.log('💾 Verified stored data:', stored);

            // Force page reload to ensure data is processed
            console.log('🔄 Forcing page reload to process Google data...');
            window.location.href = '/auth/signup';
            // this.router.navigate(['/auth/signup']);
            this.toastService.show(
              'Por favor completa tu información de registro.',
              'info'
            );
          } else if (response && response.token && response.expiresAt) {
            // Existing user, login normally
            this.authService.login(response);

            // Skip membership check for system administrators
            if (this.authService.isSystemAdmin()) {
              this.router.navigate(['/system-admin']);
              this.toastService.show(
                '¡Bienvenido! Has iniciado sesión con Google exitosamente.',
                'success'
              );
              return;
            }

            // Verify membership status before navigating
            this.membershipService.getMembershipStatus().subscribe({
              next: (status) => {
                console.log(status);
                if (status.isExpired) {
                  // Redirect to billing if membership is expired
                  this.router.navigate(['/settings/billing']);
                } else {
                  // Navigate to dashboard if membership is valid
                  this.router.navigate(['/dashboard']);
                  this.toastService.show(
                    '¡Bienvenido! Has iniciado sesión con Google exitosamente.',
                    'success'
                  );
                }
              },
              error: (err) => {
                // Only redirect to billing if it's a 402 (Payment Required) error
                // For other errors (network, server, etc.), allow normal navigation
                if (err?.status === 402) {
                  this.router.navigate(['/settings/billing']);
                } else {
                  // For other errors, navigate normally and let the guard handle it
                  console.warn(
                    'Error checking membership status, navigating normally:',
                    err
                  );
                  this.router.navigate(['/dashboard']);
                  this.toastService.show(
                    '¡Bienvenido! Has iniciado sesión con Google exitosamente.',
                    'success'
                  );
                }
              },
            });
          } else {
            this.toastService.show(
              'Respuesta inválida del servidor.',
              'warning'
            );
          }
        },
        error: (err: any) => {
          this.handleError(err);
          console.error('Error en Google Auth:', err);
        },
      });
    } catch (error) {
      console.error('Google Sign-In Error:', error);

      // If the OAuth2 popup fails, show a more specific message
      if (
        error === 'User dismissed Google Sign-In' ||
        error === 'Google authentication failed'
      ) {
        this.toastService.show(
          'Has cancelado el inicio de sesión con Google.',
          'info'
        );
      } else {
        this.toastService.show(
          'Error al iniciar sesión con Google. Intenta de nuevo.',
          'warning'
        );
      }
    }
  }

  signInWithX() {
    console.log('Sign In with X Clicked');
  }

  private handleError(error: unknown) {
    this.isLogin = false;
    this.apiErrorService.handle(error, {
      onValidationErrors: (messages) => {
        this.loginForm.markAllAsTouched();
        const body =
          '<p>Por favor verifica la siguiente información:</p>' +
          messages.map((msg) => `<p>${msg}</p>`).join('');
        this.toastService.show(body, 'warning', 6000);
      },
      onBusinessRuleErrors: (messages) => {
        this.toastService.show(messages.join('<br>'), 'warning', 6000);
      },
      onDefault: (message) => {
        this.toastService.show(message, 'warning', 5000);
      },
    });
  }
}
